import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ConfirmDialog,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import { useAuth } from "../../auth/AuthProvider";
import {
  platformApi,
  type PlatformPack,
  type PlatformTenant,
} from "../../api/platformApi";
import { toClientApiError } from "../../api/httpClient";
import { UiStateMessage } from "../../engines";
import type { UiLoadState } from "../../engines";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { BlockOutlinedIcon, MenuBookOutlinedIcon } from "../../ui/appIcons";
import { OrganizationCard } from "./OrganizationCard";
import { PlatformAiAdmin } from "../../next/ai/credits/PlatformAiAdmin";
import { Button } from "@NP-Webber/crm-kit/dist/next/index.js";
import { formatOrganizationCode } from "./orgDisplay";

function defaultAdminDisplayName(email: string): string {
  const local = email.trim().split("@")[0]?.trim() ?? "";
  return local.length > 0 ? local : "";
}

function resetCreateForm(
  setName: (v: string) => void,
  setSlug: (v: string) => void,
  setPackKey: (v: string) => void,
  setAdminEmail: (v: string) => void,
  setAdminPassword: (v: string) => void,
  setAdminDisplayName: (v: string) => void,
) {
  setName("");
  setSlug("");
  setPackKey("");
  setAdminEmail("");
  setAdminPassword("");
  setAdminDisplayName("");
}

export function OrganizationsListPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin =
    auth.user?.platformRoles?.includes("super_admin") === true;

  const [rows, setRows] = useState<PlatformTenant[]>([]);
  const [aiAdminOpen, setAiAdminOpen] = useState(false);
  const [packs, setPacks] = useState<PlatformPack[]>([]);
  const [state, setState] = useState<UiLoadState>("loading");
  const [error, setError] = useState<string | undefined>();
  const [cardOpen, setCardOpen] = useState(false);
  const [selected, setSelected] = useState<PlatformTenant | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [packKey, setPackKey] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [disableTarget, setDisableTarget] = useState<PlatformTenant | null>(null);
  const [disableBusy, setDisableBusy] = useState(false);

  const openGuidance = (organization: PlatformTenant) => {
    navigate(`/org-guidance?tenantId=${encodeURIComponent(organization.id)}`);
  };

  const columns = useMemo(
    () => [
      {
        key: "code",
        title: "קוד",
        sortable: true,
        filterable: true,
        minWidth: 100,
        render: (value: unknown) =>
          typeof value === "number" ? formatOrganizationCode(value) : "",
      },
      {
        key: "name",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 180,
      },
      {
        key: "slug",
        title: "מזהה",
        sortable: true,
        filterable: true,
        minWidth: 140,
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 160,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const org = row as unknown as PlatformTenant;
          return (
            <TableRowActions>
              <QuietButton
                title="מסמך הנחיות"
                tone="accent"
                icon={<MenuBookOutlinedIcon />}
                onClick={() => openGuidance(org)}
              />
              <QuietButton
                title="כיבוי"
                tone="danger"
                icon={<BlockOutlinedIcon />}
                onClick={() => setDisableTarget(org)}
              />
            </TableRowActions>
          );
        },
      },
    ],
    // navigate is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const load = async () => {
    setState("loading");
    setError(undefined);
    try {
      const [items, packItems] = await Promise.all([
        platformApi.listTenants(),
        platformApi.listPacks(),
      ]);
      setRows(items);
      setPacks(packItems);
      setState(items.length === 0 ? "empty" : "ready");
    } catch (err) {
      const apiError = toClientApiError(err);
      if (apiError.status === 403) {
        setState("permission");
      } else {
        setError(apiError.message);
        setState("error");
      }
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    void load();
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const openOrganization = (row: Record<string, unknown>) => {
    const org = rows.find((item) => item.id === row.id) ?? null;
    setSelected(org);
    setCardOpen(Boolean(org));
    return true;
  };

  const openCreate = () => {
    setCreateError(null);
    setCreateSuccess(null);
    resetCreateForm(
      setName,
      setSlug,
      setPackKey,
      setAdminEmail,
      setAdminPassword,
      setAdminDisplayName,
    );
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) {
      setCreateError("יש למלא שם ומזהה.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const item = await platformApi.createTenant({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        ...(packKey ? { packKey } : {}),
        ...(adminEmail.trim() && adminPassword
          ? {
              adminEmail: adminEmail.trim(),
              adminPassword,
              adminDisplayName:
                adminDisplayName.trim() ||
                defaultAdminDisplayName(adminEmail) ||
                adminEmail.trim(),
            }
          : {}),
      });
      setShowCreate(false);
      resetCreateForm(
        setName,
        setSlug,
        setPackKey,
        setAdminEmail,
        setAdminPassword,
        setAdminDisplayName,
      );
      setCreateSuccess(
        `נוצר ארגון ${item.name} (קוד ${formatOrganizationCode(item.code)})${item.packKey ? ` (חבילה: ${item.packKey})` : ""}`,
      );
      await load();
    } catch (err) {
      setCreateError(toClientApiError(err).message);
    } finally {
      setCreating(false);
    }
  };

  const handleDisableConfirm = async () => {
    if (!disableTarget) {
      return;
    }
    const disabledId = disableTarget.id;
    setDisableBusy(true);
    try {
      await platformApi.disableTenant(disabledId);
      setDisableTarget(null);
      if (selected?.id === disabledId) {
        setCardOpen(false);
        setSelected(null);
      }
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
      setDisableTarget(null);
    } finally {
      setDisableBusy(false);
    }
  };

  const packOptions = [
    { value: "none", label: "גנרי (ברירת מחדל)" },
    ...packs.map((pack) => ({
      value: pack.key,
      label: pack.displayName,
    })),
  ];

  const header = (
    <ListPageHeader
        screen
      onRefresh={() => {
        void load();
      }}
      primaryLabel="ארגון חדש"
      onPrimary={openCreate}
      secondary={
        <Button icon="sparkle" onClick={() => setAiAdminOpen(true)}>
          AI — מודלים וקרדיטים
        </Button>
      }
    />
  );

  if (state === "loading" || state === "error" || state === "permission") {
    return (
      <section className="page-section page-section--list">
        {header}
        <UiStateMessage state={state} errorMessage={error} />
      </section>
    );
  }

  return (
    <section className="page-section page-section--list">
      {header}

      {createSuccess ? <FormAlert severity="success">{createSuccess}</FormAlert> : null}

      {state === "empty" ? (
        <FormAlert severity="info">עדיין אין ארגונים.</FormAlert>
      ) : null}

      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={rows as unknown as Array<Record<string, unknown>>}
          loading={false}
          clientSideMode
          emptyMessage="עדיין אין ארגונים."
          exportFileName="organizations"
          showExport
          showFilters
          showColumnPicker
          onRowClick={openOrganization}
          onRowDoubleClick={openOrganization}
          onOpenCard={openOrganization}
        />
      </div>

      <OrganizationCard
        open={cardOpen}
        organization={selected}
        onClose={() => {
          setCardOpen(false);
          setSelected(null);
        }}
        onOpenGuidance={(organization) => {
          setCardOpen(false);
          openGuidance(organization);
        }}
        onRequestDisable={(organization) => setDisableTarget(organization)}
      />

      <ConfirmDialog
        open={Boolean(disableTarget)}
        onClose={() => {
          if (!disableBusy) setDisableTarget(null);
        }}
        title="כיבוי ארגון"
        message={
          disableTarget
            ? `לכבות את הארגון "${disableTarget.name}"? המשתמשים לא יוכלו להתחבר אליו.`
            : ""
        }
        confirmText="כיבוי"
        cancelText="חזרה"
        loading={disableBusy}
        onConfirm={() => {
          void handleDisableConfirm();
        }}
        onCancel={() => setDisableTarget(null)}
      />

      <FormDialog
        open={showCreate}
        onClose={() => {
          if (!creating) {
            setShowCreate(false);
          }
        }}
        size="md"
      >
        <FormCard
          title="יצירת ארגון"
          error={createError}
          onSave={() => {
            void handleCreate();
          }}
          onCancel={() => setShowCreate(false)}
          saveLabel="יצירה"
          cancelLabel="ביטול"
          saving={creating}
          saveDisabled={!name.trim() || !slug.trim()}
        >
          <FormSection>
            <FormGrid cols={2}>
              <FormField
                label="שם הארגון"
                required
                value={name}
                onChange={setName}
                disabled={creating}
                span={2}
              />
              <FormField
                label="מזהה (slug)"
                required
                value={slug}
                onChange={setSlug}
                disabled={creating}
                hint="אותיות קטנות באנגלית ומקפים"
              />
              <FormField
                label="חבילת הטמעה"
                type="select"
                value={packKey || "none"}
                onChange={(value) => setPackKey(value === "none" ? "" : value)}
                disabled={creating}
                options={packOptions}
                hint="גנרי = תוויות ברירת מחדל; חבילה = תוויות ופרויקטי התחלה לפי הטמעה"
              />
              <FormField
                label="אימייל מנהל (אופציונלי)"
                value={adminEmail}
                onChange={(value) => {
                  setAdminEmail(value);
                  if (
                    !adminDisplayName.trim() ||
                    adminDisplayName === defaultAdminDisplayName(adminEmail)
                  ) {
                    setAdminDisplayName(defaultAdminDisplayName(value));
                  }
                }}
                disabled={creating}
              />
              <FormField
                label="שם מנהל"
                value={adminDisplayName}
                onChange={setAdminDisplayName}
                disabled={creating}
                hint="שם התצוגה של המשתמש — לא שם הארגון"
              />
              <FormField
                label="סיסמת מנהל"
                type="password"
                value={adminPassword}
                onChange={setAdminPassword}
                disabled={creating}
                hint="נדרש אם מזינים אימייל מנהל"
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
      <PlatformAiAdmin open={aiAdminOpen} onClose={() => setAiAdminOpen(false)} />
    </section>
  );
}
